/**
 * Load ClickDimensions surveys into .regatta-cd-form roots (inline + modals).
 *
 * build.js reads window.formModel and mounts document.getElementById("root") once.
 * Injected HTML must run inline scripts first; modals use an iframe when a form already exists.
 */

const CD_LOC = 'https://analytics-eu.clickdimensions.com/vooruitzichtbe-ajkmq/pages/';
const CD_CDN = 'https://cdn-eu.clickdimensions.com/web/newformeditor/';

let builderLoadPromise = null;

/** @type {Promise<void>} */
let cdFormMountQueue = Promise.resolve();

/**
 * Serialize CD mounts — formModel / build.js are global and break when multiple forms load at once.
 *
 * @param {() => Promise<void>} task
 * @returns {Promise<void>}
 */
function enqueueCdFormMount(task) {
	const run = cdFormMountQueue.then(() => task());
	cdFormMountQueue = run.then(
		() => undefined,
		() => undefined
	);
	return run;
}

/**
 * @param {Element} root
 * @returns {boolean}
 */
function isModalClickDimensionsRoot(root) {
	return Boolean(
		root.closest('.regatta-modal-preload-host, #regatta-modal-preload, .modal-overlay')
	);
}

/**
 * Element that shows the loading overlay (wrapper survives CD root reset).
 *
 * @param {Element} root .regatta-cd-form
 * @returns {HTMLElement|null}
 */
function getClickDimensionsLoaderHost(root) {
	if (!isModalClickDimensionsRoot(root)) {
		return null;
	}

	const wrapper = root.closest('.modal-event__form, .modal-body__form');
	return wrapper instanceof HTMLElement ? wrapper : root instanceof HTMLElement ? root : null;
}

/**
 * @param {Element} root .regatta-cd-form
 * @param {boolean} loading
 * @returns {void}
 */
function setClickDimensionsRootLoading(root, loading) {
	const host = getClickDimensionsLoaderHost(root);
	if (!host) {
		return;
	}

	host.classList.toggle('regatta-cd-form-host--loading', loading);
	host.setAttribute('aria-busy', loading ? 'true' : 'false');

	let status = host.querySelector('.regatta-cd-form__loader');
	if (loading) {
		if (!status) {
			status = document.createElement('p');
			status.className = 'regatta-cd-form__loader';
			status.setAttribute('role', 'status');
			status.setAttribute('aria-live', 'polite');

			const spinner = document.createElement('span');
			spinner.className = 'regatta-cd-form__loader-spinner';
			spinner.setAttribute('aria-hidden', 'true');

			const labelEl = document.createElement('span');
			labelEl.className = 'regatta-cd-form__loader-label';
			labelEl.textContent = window.regattaModal?.formLoadingLabel || 'Formulier laden…';

			status.append(spinner, labelEl);
			host.appendChild(status);
		}
	} else if (status) {
		status.remove();
	}
}

/**
 * @returns {void}
 */
function ensureClickDimensionsGlobals() {
	window.loc = window.loc || CD_LOC;
	window.cdSurveyCdn = window.cdSurveyCdn || CD_CDN;
}

/**
 * @returns {Promise<void>}
 */
function ensureBuilderLoaded() {
	ensureClickDimensionsGlobals();

	if (window.__regattaCdBuilderReady) {
		return Promise.resolve();
	}

	if (builderLoadPromise) {
		return builderLoadPromise;
	}

	const cdn = window.cdSurveyCdn.endsWith('/') ? window.cdSurveyCdn : `${window.cdSurveyCdn}/`;
	const src = `${cdn}build.js`;

	builderLoadPromise = new Promise((resolve, reject) => {
		if (document.querySelector(`script[src="${src}"]`)) {
			window.__regattaCdBuilderReady = true;
			resolve();
			return;
		}

		const script = document.createElement('script');
		script.defer = true;
		script.src = src;
		script.onload = () => {
			window.__regattaCdBuilderReady = true;
			resolve();
		};
		script.onerror = () => reject(new Error('ClickDimensions build.js failed to load'));
		document.head.appendChild(script);
	});

	return builderLoadPromise;
}

/**
 * Run inline scripts inserted via innerHTML (sets window.formModel).
 *
 * @param {ParentNode} container
 * @returns {void}
 */
function executeInlineScripts(container) {
	container.querySelectorAll('script').forEach((oldScript) => {
		if (oldScript.src) {
			return;
		}

		const script = document.createElement('script');
		script.textContent = oldScript.textContent;
		oldScript.replaceWith(script);
	});
}

/**
 * @returns {boolean}
 */
function pageHasMountedClickDimensionsForm() {
	return Boolean(
		window.__regattaCdBuilderReady &&
			document.getElementById('root')?.querySelector('.sd-root-modern, .sd-container-modern')
	);
}

/**
 * @param {Element} root .regatta-cd-form element
 * @returns {boolean}
 */
function rootHasSurveyContent(root) {
	const iframe = root.querySelector('.regatta-cd-form__iframe');
	if (iframe instanceof HTMLIFrameElement && iframe.contentDocument) {
		return Boolean(
			iframe.contentDocument
				.getElementById('root')
				?.querySelector('.sd-root-modern, .sd-container-modern')
		);
	}

	if (root.querySelector('.sd-root-modern, .sd-container-modern')) {
		return true;
	}

	const target = root.querySelector('[pageID]');
	return Boolean(target?.querySelector('#maincontainer, #root .sd-root-modern'));
}

/**
 * @param {Element} root
 * @returns {boolean}
 */
export function clickDimensionsRootNeedsMount(root) {
	const pageId = root.getAttribute('data-cd-page-id');
	if (!pageId) {
		return false;
	}
	return !(root.dataset.cdMounted === '1' || rootHasSurveyContent(root));
}

/**
 * @param {ParentNode|null} body
 * @returns {boolean}
 */
export function modalBodyNeedsFormMount(body) {
	if (!body) {
		return false;
	}
	const roots = body.querySelectorAll('.regatta-cd-form[data-cd-page-id]');
	return Array.from(roots).some((root) => clickDimensionsRootNeedsMount(root));
}

/**
 * @param {ParentNode|null} body
 * @returns {boolean}
 */
export function modalBodyFormsHealthy(body) {
	if (!body) {
		return true;
	}
	const roots = body.querySelectorAll('.regatta-cd-form[data-cd-page-id]');
	if (!roots.length) {
		return true;
	}
	return Array.from(roots).every((root) => rootHasSurveyContent(root));
}

/**
 * @param {Element} root
 */
export function resetClickDimensionsRoot(root) {
	const pageId = root.getAttribute('data-cd-page-id') || '';
	const iframe = root.querySelector('.regatta-cd-form__iframe');
	if (iframe?._regattaCdResizeObserver) {
		iframe._regattaCdResizeObserver.disconnect();
	}

	delete root.dataset.cdMounted;
	root.innerHTML = pageId ? `<div pageID="${pageId}"></div>` : '';
}

/**
 * Mount or remount every CD root in a modal body (handles stale preloads).
 *
 * @param {ParentNode} body
 * @returns {Promise<void>}
 */
export async function ensureModalFormsMounted(body) {
	const roots = Array.from(body.querySelectorAll('.regatta-cd-form[data-cd-page-id]'));
	if (!roots.length) {
		return;
	}

	for (const root of roots) {
		const pageId = root.getAttribute('data-cd-page-id');
		if (!pageId) {
			continue;
		}

		if (rootHasSurveyContent(root)) {
			root.dataset.cdMounted = '1';
			setClickDimensionsRootLoading(root, false);
			continue;
		}

		setClickDimensionsRootLoading(root, true);
		resetClickDimensionsRoot(root);

		try {
			await loadFormIntoRoot(root, pageId);
			root.dataset.cdMounted = '1';
		} catch (err) {
			console.warn('ClickDimensions modal form mount failed:', pageId, err);
		} finally {
			setClickDimensionsRootLoading(root, false);
		}
	}

	document.dispatchEvent(
		new CustomEvent('regatta:clickdimensions-mount', { detail: { container: body } })
	);

	bindModalFormIframeSizing(body);
}

/**
 * @returns {string|null}
 */
function getClickDimensionsFormStylesheetUrl() {
	if (window.regattaModal?.cdFormCssUrl) {
		return window.regattaModal.cdFormCssUrl;
	}

	const link = document.querySelector(
		'link[rel="stylesheet"][href*="clickdimensions-form"], link[rel="stylesheet"][href*="/dist/css/main"]'
	);

	return link?.href ?? null;
}

/**
 * @param {HTMLIFrameElement} iframe
 * @returns {boolean}
 */
function iframeLivesInModalSplitLayout(iframe) {
	return Boolean(
		iframe.closest(
			'.modal-body--split .modal-event__main, .modal-event--split .modal-event__main, .modal-event__content .modal-event__form, .modal-column--main'
		)
	);
}

/**
 * @param {HTMLIFrameElement} iframe
 * @returns {boolean}
 */
function iframeLivesInModal(iframe) {
	return Boolean(
		iframe.closest('.modal-overlay, .regatta-modal-preload-host, [class*="modal-body"]')
	);
}

/**
 * @param {boolean} isSplit Modal split column (fixed iframe height, scroll inside document).
 * @param {boolean} isModal Form rendered inside a modal (iframe or inline).
 * @returns {string}
 */
function getIframeLayoutCss(isSplit, isModal) {
	let css = '';

	if (isSplit) {
		css += `
			html, body {
				height: 100%;
				margin: 0;
				overflow: auto !important;
				-webkit-overflow-scrolling: touch;
			}
			.sd-body.sd-body--responsive { overflow: visible !important; }
		`;
	} else {
		css += `
			html, body { overflow: visible !important; min-height: 0; }
			.sd-body.sd-body--responsive { overflow: visible !important; }
		`;
	}

	if (isModal) {
		css += `
			:is(#root .sd-root-modern) .sd-body.sd-body--responsive {
				padding-top: 0;
				padding-inline: 0 !important;
			}
			:is(#root .sd-root-modern) .sd-page.sd-body__page {
				margin-block-start: 0 !important;
			}
		`;
	}

	return css;
}

/**
 * @param {HTMLIFrameElement} iframe
 * @returns {HTMLElement|null}
 */
function getSplitModalFormShell(iframe) {
	const formWrap =
		iframe.closest('.modal-event__form') || iframe.closest('.modal-body__form');

	if (formWrap instanceof HTMLElement) {
		return formWrap;
	}

	const cdRoot = iframe.closest('.regatta-cd-form');
	if (cdRoot?.parentElement instanceof HTMLElement) {
		return cdRoot.parentElement;
	}

	const column = iframe.closest('.modal-column--main');
	return column instanceof HTMLElement ? column : null;
}

/**
 * Size split-modal iframes to the form column (pixel height; % can fail after preload).
 *
 * @param {HTMLIFrameElement} iframe
 * @returns {void}
 */
function syncSplitModalIframeHeight(iframe) {
	if (!iframeLivesInModalSplitLayout(iframe)) {
		return;
	}

	const shell = getSplitModalFormShell(iframe);
	if (!shell) {
		return;
	}

	const h = shell.clientHeight;
	if (h > 0) {
		iframe.style.height = `${h}px`;
		iframe.style.minHeight = '0';
	}
}

/**
 * Copy theme CSS variables + form overrides into an iframe document.
 *
 * @param {Document} doc
 * @param {HTMLIFrameElement|null} iframe Host iframe (split vs auto-height layout).
 * @returns {void}
 */
function injectIframeThemeStyles(doc, iframe = null) {
	const head = doc.head;
	if (!head) {
		return;
	}

	const parentVars = getComputedStyle(document.documentElement);
	const decls = [];

	for (let i = 0; i < parentVars.length; i += 1) {
		const name = parentVars[i];
		if (name.startsWith('--wp--')) {
			decls.push(`${name}:${parentVars.getPropertyValue(name).trim()}`);
		}
	}

	if (decls.length && !doc.getElementById('regatta-cd-theme-vars')) {
		const varStyle = doc.createElement('style');
		varStyle.id = 'regatta-cd-theme-vars';
		varStyle.textContent = `:root{${decls.join(';')}}`;
		head.insertBefore(varStyle, head.firstChild);
	}

	const isSplit =
		iframe instanceof HTMLIFrameElement && iframeLivesInModalSplitLayout(iframe);
	const isModal = iframe instanceof HTMLIFrameElement && iframeLivesInModal(iframe);
	let layoutStyle = doc.getElementById('regatta-cd-iframe-layout');

	if (!layoutStyle) {
		layoutStyle = doc.createElement('style');
		layoutStyle.id = 'regatta-cd-iframe-layout';
		head.appendChild(layoutStyle);
	}

	layoutStyle.textContent = getIframeLayoutCss(isSplit, isModal);

	const href = getClickDimensionsFormStylesheetUrl();
	if (href && !doc.querySelector('link[data-regatta-cd-theme]')) {
		const link = doc.createElement('link');
		link.rel = 'stylesheet';
		link.href = href;
		link.setAttribute('data-regatta-cd-theme', '1');
		head.insertBefore(link, head.firstChild);
	}
}

function resizeClickDimensionsIframe(iframe) {
	const doc = iframe.contentDocument;
	if (!doc?.body) {
		return;
	}

	if (iframeLivesInModalSplitLayout(iframe)) {
		syncSplitModalIframeHeight(iframe);
		return;
	}

	const height = Math.max(doc.body.scrollHeight, doc.documentElement?.scrollHeight ?? 0);
	iframe.style.height = `${height}px`;
}

/**
 * Keep split-modal iframes sized to their form column (event + download modals).
 *
 * @param {ParentNode} container Modal overlay or body.
 */
export function bindModalFormIframeSizing(container = document) {
	const iframes = container.querySelectorAll(
		'.modal-event--split .modal-event__form .regatta-cd-form__iframe, .modal-column--main .regatta-cd-form__iframe, .modal-body--split .modal-event__main .regatta-cd-form__iframe'
	);

	iframes.forEach((iframe) => {
		if (!(iframe instanceof HTMLIFrameElement)) {
			return;
		}

		const shell = getSplitModalFormShell(iframe);
		const column =
			iframe.closest('.modal-event__main') || iframe.closest('.modal-column--main');

		const resize = () => {
			resizeClickDimensionsIframe(iframe);
			const doc = iframe.contentDocument;
			if (doc) {
				injectIframeThemeStyles(doc, iframe);
			}
		};

		resize();

		if (iframe._regattaFormResizeObserver) {
			iframe._regattaFormResizeObserver.disconnect();
		}

		const targets = [shell, column].filter((el) => el instanceof HTMLElement);
		if (!targets.length) {
			return;
		}

		const observer = new ResizeObserver(resize);
		targets.forEach((el) => observer.observe(el));
		iframe._regattaFormResizeObserver = observer;
	});
}

/**
 * @param {Element} root
 * @param {string} pageId
 * @param {string} surveyHtml Full .html document from ClickDimensions.
 * @returns {Promise<void>}
 */
async function loadFormInIframe(root, pageId, surveyHtml) {
	root.innerHTML = '';

	const iframe = document.createElement('iframe');
	iframe.className = 'regatta-cd-form__iframe';
	iframe.title = 'Formulier';
	iframe.setAttribute('loading', 'lazy');
	iframe.style.width = '100%';
	iframe.style.border = '0';
	iframe.style.display = 'block';

	root.appendChild(iframe);

	await new Promise((resolve, reject) => {
		iframe.onload = () => resolve();
		iframe.onerror = () => reject(new Error(`ClickDimensions iframe failed for ${pageId}`));

		const doc = iframe.contentDocument;
		if (!doc) {
			reject(new Error('ClickDimensions iframe document unavailable'));
			return;
		}

		doc.open();
		doc.write(surveyHtml);
		doc.close();
		injectIframeThemeStyles(doc, iframe);
	});

	const doc = iframe.contentDocument;
	if (!doc) {
		return;
	}

	await new Promise((resolve) => {
		if (doc.getElementById('root')?.querySelector('.sd-root-modern, .sd-container-modern')) {
			resolve();
			return;
		}

		const rootObserver = new MutationObserver(() => {
			if (doc.getElementById('root')?.querySelector('.sd-root-modern, .sd-container-modern')) {
				rootObserver.disconnect();
				resolve();
			}
		});

		if (doc.body) {
			rootObserver.observe(doc.body, { childList: true, subtree: true });
		}

		window.setTimeout(() => {
			rootObserver.disconnect();
			resolve();
		}, 20000);
	});

	injectIframeThemeStyles(doc, iframe);
	resizeClickDimensionsIframe(iframe);

	document.dispatchEvent(
		new CustomEvent('regatta:clickdimensions-iframe-ready', {
			detail: { iframe, root, pageId },
		})
	);

	if (!iframeLivesInModalSplitLayout(iframe)) {
		const observer = new ResizeObserver(() => resizeClickDimensionsIframe(iframe));
		if (doc.body) {
			observer.observe(doc.body);
		}
		iframe._regattaCdResizeObserver = observer;
	}
}

/**
 * @param {Element} root
 * @param {string} pageId
 * @param {string} surveyHtml
 * @returns {Promise<void>}
 */
async function loadFormInline(root, pageId, surveyHtml) {
	ensureClickDimensionsGlobals();

	const parsed = document.createElement('div');
	parsed.innerHTML = surveyHtml;

	const main = parsed.querySelector('#maincontainer');
	const cdAnalytics = parsed.querySelector('#cdWidgetAnalytics')?.outerHTML ?? '';

	let target = root.querySelector('[pageID]');
	if (!target) {
		target = document.createElement('div');
		target.setAttribute('pageID', pageId);
		root.appendChild(target);
	} else if (!target.getAttribute('pageID')) {
		target.setAttribute('pageID', pageId);
	}

	target.innerHTML = (main ? main.outerHTML : '') + cdAnalytics;
	executeInlineScripts(target);

	if (!window.formModel) {
		throw new Error(`ClickDimensions formModel missing for ${pageId}`);
	}

	if (window.clickDimensionsVisitorKey && window.cdAnalytics?.setForms) {
		window.cdAnalytics.setForms(window.clickDimensionsVisitorKey);
	}

	await ensureBuilderLoaded();
}

/**
 * @param {Element} root
 * @param {string} pageId
 * @returns {Promise<void>}
 */
async function loadFormIntoRoot(root, pageId) {
	return enqueueCdFormMount(async () => {
		ensureClickDimensionsGlobals();

		const loc = window.loc.endsWith('/') ? window.loc : `${window.loc}/`;
		const response = await fetch(`${loc}${pageId}.html`);

		if (!response.ok) {
			throw new Error(`ClickDimensions form ${pageId} failed (${response.status})`);
		}

		const surveyHtml = await response.text();
		// Modals always use iframes (isolates formModel). Page contact form stays inline when first.
		const useIframe =
			isModalClickDimensionsRoot(root) || pageHasMountedClickDimensionsForm();

		if (useIframe) {
			await loadFormInIframe(root, pageId, surveyHtml);
			return;
		}

		await loadFormInline(root, pageId, surveyHtml);
	});
}

/**
 * Legacy export — configures globals only.
 *
 * @returns {Promise<void>}
 */
export function ensureClickDimensionsReady() {
	ensureClickDimensionsGlobals();
	window.__regattaCdReady = true;
	return Promise.resolve();
}

/**
 * Mount CD widgets for roots inside a container (or document).
 *
 * @param {ParentNode} container
 * @returns {Promise<void>}
 */
export async function mountClickDimensionsRoots(container = document) {
	const roots = container.querySelectorAll('.regatta-cd-form[data-cd-page-id]');
	if (!roots.length) {
		return;
	}

	ensureClickDimensionsGlobals();

	let mountedAny = false;

	for (const root of roots) {
		if (root.dataset.cdMounted === '1' || rootHasSurveyContent(root)) {
			root.dataset.cdMounted = '1';
			mountedAny = true;
			continue;
		}

		const pageId = root.getAttribute('data-cd-page-id');
		if (!pageId) {
			continue;
		}

		try {
			await loadFormIntoRoot(root, pageId);
			root.dataset.cdMounted = '1';
			mountedAny = true;
		} catch (err) {
			console.warn('ClickDimensions form mount failed:', pageId, err);
		}
	}

	if (mountedAny) {
		document.dispatchEvent(
			new CustomEvent('regatta:clickdimensions-mount', { detail: { container } })
		);
	}
}
